/**
 * Centralized color palette for athlete charts
 * 
 * This module provides a consistent 30-color palette used across all athlete visualizations
 * in the coach dashboard. Colors are chosen to be:
 * - Visually distinct from each other
 * - Accessible (WCAG AA compliant)
 * - Suitable for both light and dark modes
 * - Evenly distributed across the color spectrum
 * 
 * Usage:
 * - Use `CHART_COLORS` for simple line/bar charts
 * - Use `CHART_COLORS_WITH_BACKGROUND` for charts that need border and background variants
 * - Use `getChartColor(index)` to get a color by index (automatically wraps if index > 30)
 */

/**
 * Primary color palette with 30 distinct, accessible colors
 * Colors are ordered to maximize visual distinction between adjacent athletes
 */
export const CHART_COLORS: string[] = [
  '#DC2626', // Red
  '#2563EB', // Blue
  '#059669', // Green
  '#D97706', // Orange
  '#7C3AED', // Purple
  '#DB2777', // Pink
  '#0891B2', // Cyan
  '#65A30D', // Lime
  '#C026D3', // Fuchsia
  '#EA580C', // Deep Orange
  '#0D9488', // Teal
  '#4F46E5', // Indigo
  '#BE123C', // Rose
  '#0284C7', // Sky Blue
  '#16A34A', // Emerald
  '#A855F7', // Violet
  '#E11D48', // Crimson
  '#0369A1', // Light Blue
  '#84CC16', // Yellow Green
  '#9333EA', // Deep Purple
  // 10 additional colors to reach 30
  '#B91C1C', // Dark Red
  '#1D4ED8', // Dark Blue
  '#047857', // Dark Green
  '#B45309', // Dark Orange
  '#6D28D9', // Dark Purple
  '#BE185D', // Dark Pink
  '#0E7490', // Dark Cyan
  '#4D7C0F', // Dark Lime
  '#A21CAF', // Dark Fuchsia
  '#C2410C', // Burnt Orange
];

/**
 * Color palette with border and background variants for filled charts
 * Each entry contains a border color and semi-transparent background
 */
export const CHART_COLORS_WITH_BACKGROUND = CHART_COLORS.map(color => ({
  border: color,
  bg: `${color}1A`, // Adds 10% opacity (1A in hex = 26/255 ≈ 10%)
}));

/**
 * Get a chart color by index
 * Automatically wraps around if index exceeds palette size
 * 
 * @param index - The index of the color to retrieve
 * @returns The color hex code
 */
export const getChartColor = (index: number): string => {
  return CHART_COLORS[index % CHART_COLORS.length];
};

/**
 * Get a chart color with background variant by index
 * Automatically wraps around if index exceeds palette size
 * 
 * @param index - The index of the color to retrieve
 * @returns Object with border and bg color hex codes
 */
export const getChartColorWithBackground = (index: number): { border: string; bg: string } => {
  return CHART_COLORS_WITH_BACKGROUND[index % CHART_COLORS_WITH_BACKGROUND.length];
};

/**
 * Generate a semi-transparent background color from a hex color
 * 
 * @param hexColor - The hex color code (e.g., '#DC2626')
 * @param opacity - The opacity value between 0 and 1 (default: 0.1)
 * @returns RGBA color string
 */
export const hexToRgba = (hexColor: string, opacity: number = 0.1): string => {
  // Remove the hash if present
  const hex = hexColor.replace('#', '');
  
  // Parse the hex color
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Build a stable athlete→color map from a list of athlete names.
 * Names are deduplicated and sorted so every chart gets the same
 * color for the same athlete regardless of which data source the
 * chart reads from.
 */
export function buildAthleteColorMap(names: string[]): Map<string, string> {
    const map = new Map<string, string>();
    [...new Set(names)].sort().forEach((name, i) => {
        map.set(name, CHART_COLORS[i % CHART_COLORS.length]);
    });
    return map;
}

/**
 * Total number of colors available in the palette
 */
export const TOTAL_COLORS = CHART_COLORS.length;

